import * as React from 'react';

import { DeviceList } from './components/device-list';
import { DeviceInstruction, DeviceInstructionModel } from './components/device-instruction';
import { ErrorBoundary } from '../errors/ErrorBoundary';
import Alert, { AlertCategoryEnum } from '../misc/components/Alert';
import { useCurrentStudyConfigurationContext } from '../Core/CurrentStudyConfigurationContext';
import { Modal } from '../misc/components/modal';
import { useWebPortalLegacyClient } from '../misc/api/WebPortalTokenClient';


export interface CannotFindDeviceWizardProps {
    onFinishClick: () => void;
}

declare const rootUrl: string;

export const CannotFindDeviceWizard = (props: CannotFindDeviceWizardProps) => {
    const { study } = useCurrentStudyConfigurationContext();
    const [devices, setDevices] = React.useState([]);
    const [fetchingDevices, setFetchingDevices] = React.useState<boolean>(false);
    const [currentStep, setCurrentStep] = React.useState<number>(1);
    const [isError, setIsError] = React.useState<boolean>(false);
    const [selectedDeviceId, setSelectedDeviceId] = React.useState<string>();
    const [deviceInstructions, setDeviceInstructions] = React.useState<DeviceInstructionModel>();
    const [fetchingInstructions, setFetchingInstructions] = React.useState<boolean>(false);
    const legacyClient = useWebPortalLegacyClient();

    React.useEffect(() => {
        const fetchDeviceList = () => {
            setFetchingDevices(true);
            return legacyClient.getHubStudyDeviceTypes(study.id)
                .then(json => setDevices(json))
                .catch(() => setIsError(true))
                .finally(() => setFetchingDevices(false));
        }

        fetchDeviceList();
    }, [study]);

    React.useEffect(() => {
        const fetchInstructions = () => {
            if (selectedDeviceId) {
                setFetchingInstructions(true);
                return legacyClient.getStudyDeviceInstructions(selectedDeviceId)
                    .then(json => setDeviceInstructions(json))
                    .catch(() => setIsError(true))
                    .finally(() => setFetchingInstructions(false));
            }
        }

        fetchInstructions();
    }, [selectedDeviceId]);

    const renderPage = (page) => {
        if (!isError) {
            switch (page) {
                case 1:
                    return (<DeviceList
                        isFetching={fetchingDevices}
                        devices={devices}
                        selectedOption={selectedDeviceId}
                        handleChange={(event) => setSelectedDeviceId(event.target.value)} />);
                case 2:
                    return (<DeviceInstruction isFetching={fetchingInstructions} instructions={deviceInstructions} />);
            }
            return null;
        } else {
            return <Alert category={AlertCategoryEnum.Danger} show={true}>A error occurred retrieving specific instructions for your device</Alert>;
        }
    }

    const onPrevious = () => {
        setCurrentStep(currentStep - 1);
    }

    const onNext = () => {
        setCurrentStep(currentStep + 1);
    }

    return (
        <div>
            <div className="modal-backdrop fade in"></div>
            <Modal show={true} header="Device Connection Instructions" closeModalHandler={props.onFinishClick}>
                <div className="modal-body">
                    <ErrorBoundary>
                        {renderPage(currentStep)}
                    </ErrorBoundary>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-primary" disabled={currentStep === 1} onClick={onPrevious}>Previous</button>
                    <button className="btn btn-primary" disabled={!selectedDeviceId || currentStep === 2} onClick={onNext}>Next</button>
                    <button className="btn btn-primary" disabled={currentStep < 2} onClick={props.onFinishClick}>Finish</button>
                </div>
            </Modal>
        </div>
    );
}
